import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { EnvironmentService } from './core/services/environment.service';
import { FormsModule } from '@angular/forms';
import { SafePipe } from './core/shared/safe.pipe';
import { PortalComponent } from './features/portal/portal.component';
import { LoginComponent } from './features/login/login.component';
import { ToolbarComponent } from './features/toolbar/toolbar.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UserProfileComponent } from './features/user-profile/user-profile.component';
import { AppSwitcherComponent } from './features/app-switcher/app-switcher.component';
import { NotificationComponent } from './features/notification/notification.component';
import { ClientLoginDialogComponent } from './features/client-login-dialog/client-login-dialog.component';
import { LOCATION_TOKEN } from './core/constants/location.token';
import { ErrorDialogComponent } from './core/shared/error/error-dialog.component';
import { ToastMessageComponent } from './core/shared/toast-message/toast-message.component';
import { SiteSwitcherMenuComponent } from './features/site-switcher/site-switcher-menu.component';
import { PortalDevModule } from './features/dev-only/portal-dev.module';
import { LandingPageModule } from './features/landing-page/landing-page.module';
import { SidenavComponent } from './features/sidenav/sidenav.component';
import { RequestIdInterceptor } from './interceptors/request-id-interceptor';
import { ReminderDialogComponent } from './core/shared/reminder-dialog/reminder-dialog.component';
import { MaterialModule } from './material.module';
import {catchError, of, switchMap, take, tap} from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { NewMenuItemComponent } from './features/sidenav/menu.component';
import { MenuComponent } from './features/toolbar/menu/menu.component';
import { SecondaryToolbarComponent } from './features/toolbar/secondary-toolbar.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ViewTypeSelectComponent } from './features/view-type-select/view-type-select.component';
import { MobileMenuComponent } from './features/mobile-menu/mobile-menu.component';
import { JuniperSidecarOverlayComponent } from './features/portal/iframe-overlay/juniper-sidecar-overlay.component';
import { LoadErrorComponent } from './features/load-error/load-error.component';
import { ConsoleLogger } from './core/shared/console-logger';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash-es';

@NgModule({
  declarations: [
    AppComponent,
    AppSwitcherComponent,
    ClientLoginDialogComponent,
    LoginComponent,
    NotificationComponent,
    PortalComponent,
    SafePipe,
    ToolbarComponent,
    SecondaryToolbarComponent,
    UserProfileComponent,
    SiteSwitcherMenuComponent,
    ToastMessageComponent,
    ErrorDialogComponent,
    SidenavComponent,
    ReminderDialogComponent,
    ViewTypeSelectComponent,
    NewMenuItemComponent,
    MenuComponent,
    MobileMenuComponent,
    JuniperSidecarOverlayComponent,
    LoadErrorComponent
  ],
  exports: [SafePipe],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    MaterialModule,
    PortalDevModule,
    FontAwesomeModule,
    LandingPageModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [EnvironmentService, AuthService],
      useFactory: (envService: EnvironmentService, authService: AuthService) => {
        return () => {
          ConsoleLogger.debug('init: begin');
          let bxid = localStorage.getItem('bxid');
          if (_.isEmpty(bxid)) {
            bxid = uuidv4();
            localStorage.setItem('bxid',  bxid);
          }
          envService.bxid = bxid;

          return envService.loadAppConfig()
            .pipe(
              switchMap(() => {
                ConsoleLogger.debug('init: config loaded');
                return authService.setUserLoginModelFromLocalStorageIfNotExists();
              }),
              catchError((e) => {
                ConsoleLogger.error('failed to load app config', e);
                return of(undefined);
              }),
              switchMap(() => {
                ConsoleLogger.debug('init: ulm check complete');
                return envService.checkDebuggingEnabled(authService.getUserLogin()); // don't worry about the userguid, this will also check the bxId
              }),
              tap(() => {
                ConsoleLogger.debug('init: complete');
              }),
              take(1)
            );
        };
      }
    },
    { provide: LOCATION_TOKEN, useValue: window.location },
    { provide: HTTP_INTERCEPTORS, useClass: RequestIdInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi())
  ]
})

export class AppModule {
}
